/**
 * @file useWmsDocumentsList.ts
 * @description Данные списков складских документов — приёмки, списания и
 * перемещения (phase19.23 этапы A и B).
 *
 * Фильтры считает сервер (`warehouseId` / `status` / `search` / `reason` /
 * `source` / период по `createdAt`),
 * страницы приходят по 20 и дописываются кнопкой «Показать ещё»: журналы
 * растут бесконечно, а клиентский фильтр по 100 последним документам врал —
 * «ничего не нашлось» означало «нет на первой сотне».
 *
 * Вид документа — ключ реестра загрузчиков, а не ветка `if`: третий журнал
 * добавляется записью в `LOADERS`.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { useNotify } from "../ui/NotifyProvider";
import {
  transfersApi,
  type TransferListItemDto,
  type TransferSource,
  type TransferStatus,
} from "../../lib/api-transfers";
import {
  receiptsApi,
  warehousesApi,
  writeoffsApi,
  type GoodsReceiptDto,
  type WarehouseDto,
  type WmsDocumentsPage,
  type WriteoffDto,
} from "../../lib/api-wms";
import { wmsErrorText } from "../../lib/wms-error-text";
import { WMS_PERIOD_DEFAULT, wmsPeriodFrom } from "../../lib/wms-period";

/** Вид журнала → тип документа в нём. */
interface DocumentByKind {
  receipts: GoodsReceiptDto;
  writeoffs: WriteoffDto;
  transfers: TransferListItemDto;
}

/** Параметры запроса страницы — общие для журналов. */
interface LoadParams {
  warehouseId?: string;
  status?: string;
  search?: string;
  /** Причина списания (журнал списаний). */
  reason?: string;
  /**
   * Виды документа через запятую (журнал перемещений). Пусто — параметр не
   * шлём: сервер сам прячет быстрые действия.
   */
  source?: string;
  /** Начало периода (ISO); конца нет — «до сих пор» (см. `wms-period.ts`). */
  from?: string;
  page: number;
  pageSize: number;
}

/** Реестр загрузчиков: новый журнал = запись, не ветка. */
const LOADERS: {
  [K in keyof DocumentByKind]: (p: LoadParams) => Promise<WmsDocumentsPage<DocumentByKind[K]>>;
} = {
  receipts: (p) => receiptsApi.listPage(p),
  writeoffs: (p) => writeoffsApi.listPage(p),
  // У перемещения склад в фильтре — это склад-ИСТОЧНИК: целевых складов в
  // документе может быть несколько (они живут в строках).
  transfers: (p) =>
    transfersApi.listPage({
      search: p.search,
      status: p.status as TransferStatus | undefined,
      fromWarehouseId: p.warehouseId,
      // Строка «MANUAL,PLACEMENT,QUICK_ACTION» из чипса «Показать быстрые»:
      // клиент зеркалит серверный разбор списка видов.
      source: p.source
        ? (p.source.split(",").filter(Boolean) as TransferSource[])
        : undefined,
      from: p.from,
      page: p.page,
      pageSize: p.pageSize,
    }),
};

const PAGE_SIZE = 20;

/** Пауза перед запросом при наборе — не дёргаем сервер на каждую букву. */
const SEARCH_DEBOUNCE_MS = 300;

/** Начальные значения фильтров — приходят из адреса (ссылки с дашборда). */
interface InitialFilters {
  /** Статус документа: `?status=DRAFT` — плитка «Черновики приёмок» дашборда склада. */
  status?: string;
  /**
   * Склад: `?wh=` из той же ссылки. Без него плитка «Черновики приёмок: 2»
   * выбранного склада открывала черновики всех складов магазина (правка по
   * ревью 2026-09-20).
   */
  warehouseId?: string;
}

export function useWmsDocumentsList<K extends keyof DocumentByKind>(
  kind: K,
  errorText: string,
  initial: InitialFilters = {},
) {
  const notify = useNotify();
  const [items, setItems] = useState<DocumentByKind[K][]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseDto[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Значение берём только при первом рендере: дальше фильтром управляет сам
  // реестр, и возврат «Назад» не должен перетирать выбор пользователя.
  const [status, setStatus] = useState(initial.status ?? "");
  const [warehouseId, setWarehouseId] = useState(initial.warehouseId ?? "");
  const [reason, setReason] = useState("");
  // Источник документа: пусто — сервер сам прячет быстрые действия.
  const [source, setSource] = useState("");
  // Период по `createdAt`: журнал растёт бесконечно, по умолчанию 30 дней.
  // Пока его шлёт только реестр перемещений — остальные загрузчики параметр
  // игнорируют, селект у них не отрисован.
  const [period, setPeriod] = useState<string>(WMS_PERIOD_DEFAULT);
  // Границу фиксируем на смену периода, а не на каждый запрос: иначе «Показать
  // ещё» считал бы вторую страницу от сдвинувшегося окна и строки на стыке
  // задвоились бы или пропали.
  const periodFrom = useMemo(() => wmsPeriodFrom(period), [period]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(
    async (nextPage: number) => {
      const first = nextPage === 1;
      if (first) setLoading(true);
      else setLoadingMore(true);
      try {
        // Реестр типизирован по ключу, но TS сужает union только через cast:
        // тип результата у обоих загрузчиков — страница своего документа.
        const loader = LOADERS[kind] as (
          p: LoadParams,
        ) => Promise<WmsDocumentsPage<DocumentByKind[K]>>;
        const res = await loader({
          warehouseId: warehouseId || undefined,
          status: status || undefined,
          search: debouncedSearch || undefined,
          reason: reason || undefined,
          source: source || undefined,
          from: periodFrom,
          page: nextPage,
          pageSize: PAGE_SIZE,
        });
        // Первая страница заменяет список, следующие дописываются.
        setItems((prev) => (first ? res.items : [...prev, ...res.items]));
        setTotal(res.total);
        setPage(res.page);
        setError(null);
      } catch (err) {
        setError(wmsErrorText(err, errorText));
      } finally {
        if (first) setLoading(false);
        else setLoadingMore(false);
      }
    },
    [kind, warehouseId, status, debouncedSearch, reason, source, periodFrom, errorText],
  );

  // Любая смена фильтра — запрос с первой страницы (фильтры серверные).
  useEffect(() => {
    void load(1);
  }, [load]);

  useEffect(() => {
    warehousesApi
      .list()
      .then(setWarehouses)
      .catch(() => {
        // Без справочника фильтр по складу выглядит пустым, как будто складов
        // нет — говорим об этом вслух.
        setWarehouses([]);
        notify.error("Не удалось загрузить склады, фильтр недоступен");
      });
  }, [notify]);

  const filtering = !!(debouncedSearch || status || warehouseId || reason || source);
  const hasMore = items.length < total;

  /** «Сбросить фильтры» пустой выдачи: период не трогаем — у него своя кнопка. */
  function reset() {
    setSearch("");
    setDebouncedSearch("");
    setStatus("");
    setWarehouseId("");
    setReason("");
    setSource("");
  }

  return {
    items,
    warehouses,
    search,
    setSearch,
    status,
    setStatus,
    warehouseId,
    setWarehouseId,
    reason,
    setReason,
    source,
    setSource,
    period,
    setPeriod,
    total,
    loading,
    loadingMore,
    error,
    filtering,
    hasMore,
    loadMore: () => void load(page + 1),
    /** «Повторить» после ошибки загрузки — с первой страницы. */
    reload: () => void load(1),
    reset,
  };
}
